import { path } from './base';
import { QueryClient, useMutation, useQuery, useQueryClient, type QueryClientConfig } from '@tanstack/react-query';
import {
  parseApprovalsPage,
  parseBadges,
  parseConfig,
  parseExperiments,
  parseChangeLog,
  parseChecksSection,
  parseCheckFindingsPage,
  parseItemStatus,
  parseItemStacks,
  parseObjectives,
  parseStats,
  parseTactics,
  parseTacticExperiments,
  parseTacticDraftsPage,
  type ApprovalsPage,
  type ApprovalsQuery,
  type ChangeLogEvent,
  type MachineChecksSection,
  type MachineCheckFindingsPage,
  type ItemStatus,
  type ItemStack,
  type MachineConfig,
  type MachineExperiment,
  type MachineStats,
  type ObjectivesSection,
  type TacticDefinition,
  type TacticExperiment,
  type TacticDraftsPage,
  type TacticDraftsQuery,
} from './cockpit-data';

type FetchImpl = typeof fetch;

// Serialize an approvals query to a stable (sorted-key) query string, so equal
// queries produce equal URLs and equal react-query keys.
export function approvalsSearch(query: ApprovalsQuery): string {
  const search = new URLSearchParams();
  for (const key of ['scope', 'status', 'domino', 'market', 'action', 'q', 'limit', 'offset'] as const) {
    const value = query[key];
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  search.sort();
  return search.toString();
}

export async function fetchApprovalsPage(query: ApprovalsQuery, fetchImpl: FetchImpl = fetch): Promise<ApprovalsPage> {
  const qs = approvalsSearch(query);
  const response = await fetchImpl(path(`/state/approvals${qs ? `?${qs}` : ''}`));
  if (response.status >= 400) {
    throw new Error(`Approvals request failed with status ${response.status}`);
  }
  return parseApprovalsPage(await response.json());
}

export type ObjectivesRange = { start: string; end: string };

export async function fetchObjectives(
  range: ObjectivesRange | null,
  fetchImpl: FetchImpl = fetch,
): Promise<ObjectivesSection> {
  const query = range ? `?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}` : '';
  const response = await fetchImpl(path(`/state/objectives${query}`));
  if (!response.ok) {
    throw new Error(`Objectives request failed with status ${response.status}`);
  }
  return parseObjectives(await response.json());
}

export async function fetchStats(fetchImpl: FetchImpl = fetch): Promise<MachineStats | null> {
  const response = await fetchImpl(path("/state/stats"));
  if (response.status >= 400) {
    throw new Error(`Stats request failed with status ${response.status}`);
  }
  return parseStats(await response.json());
}

export async function fetchConfig(
  opts: { fresh?: boolean } = {},
  fetchImpl: FetchImpl = fetch,
): Promise<MachineConfig | null> {
  const response = await fetchImpl(path(`/state/config${opts.fresh ? '?fresh=1' : ''}`));
  if (response.status >= 400) {
    throw new Error(`Config request failed with status ${response.status}`);
  }
  return parseConfig(await response.json());
}

export async function fetchExperiments(fetchImpl: FetchImpl = fetch): Promise<MachineExperiment[]> {
  const response = await fetchImpl(path("/state/experiments"));
  if (response.status >= 400) {
    throw new Error(`Experiments request failed with status ${response.status}`);
  }
  return parseExperiments(await response.json());
}

export async function fetchItemStacks(fetchImpl: FetchImpl = fetch): Promise<ItemStack[]> {
  const response = await fetchImpl(path("/state/item-stacks"));
  if (response.status >= 400) {
    throw new Error(`Item stacks request failed with status ${response.status}`);
  }
  return parseItemStacks(await response.json());
}

export async function fetchItemStatus(fetchImpl: FetchImpl = fetch): Promise<ItemStatus[]> {
  const response = await fetchImpl(path("/state/item-status"));
  if (response.status >= 400) {
    throw new Error(`Keyword status request failed with status ${response.status}`);
  }
  return parseItemStatus(await response.json());
}

export async function fetchChangeLog(fetchImpl: FetchImpl = fetch): Promise<ChangeLogEvent[]> {
  const response = await fetchImpl(path("/state/change-log"));
  if (response.status >= 400) {
    throw new Error(`Change log request failed with status ${response.status}`);
  }
  return parseChangeLog(await response.json());
}

export async function fetchTactics(fetchImpl: FetchImpl = fetch): Promise<TacticDefinition[]> {
  const response = await fetchImpl(path("/state/tactics"));
  if (response.status >= 400) {
    throw new Error(`Tactics request failed with status ${response.status}`);
  }
  return parseTactics(await response.json());
}

export async function fetchTacticExperiments(fetchImpl: FetchImpl = fetch): Promise<TacticExperiment[]> {
  const response = await fetchImpl(path("/state/tactic-experiments"));
  if (response.status >= 400) {
    throw new Error(`Tactic experiments request failed with status ${response.status}`);
  }
  return parseTacticExperiments(await response.json());
}

export function tacticDraftsSearch(query: TacticDraftsQuery): string {
  const search = new URLSearchParams();
  for (const key of ['signal_id', 'market', 'tier', 'limit', 'offset'] as const) {
    const value = query[key];
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  search.sort();
  return search.toString();
}

export async function fetchTacticDraftsPage(
  query: TacticDraftsQuery,
  fetchImpl: FetchImpl = fetch,
): Promise<TacticDraftsPage> {
  const search = tacticDraftsSearch(query);
  const response = await fetchImpl(path(`/state/tactic-drafts${search ? `?${search}` : ''}`));
  if (response.status >= 400) throw new Error(`Tactic drafts request failed with status ${response.status}`);
  return parseTacticDraftsPage(await response.json());
}

export type TacticDraftAction = 'apply' | 'change' | 'remove';

export type TacticDraftActionResult = {
  ok: boolean;
  status: number;
  body: Record<string, unknown>;
};

export async function postTacticDraftAction(
  action: TacticDraftAction,
  draftId: string,
  runAsExperiment: boolean | undefined,
  fetchImpl: FetchImpl = fetch,
): Promise<TacticDraftActionResult> {
  const body = {
    draft_id: draftId,
    ...(runAsExperiment === undefined ? {} : { run_as_experiment: runAsExperiment }),
  };
  const response = await fetchImpl(path(`/tactic/${action}`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await response.json().catch(() => ({ error: 'bad_response_json' })) as Record<string, unknown>;
  return { ok: response.ok, status: response.status, body: result };
}

export async function fetchBadges(
  fetchImpl: FetchImpl = fetch,
): Promise<{ review: number; defer: number; maintain: number }> {
  const response = await fetchImpl(path("/state/badges"));
  if (response.status >= 400) {
    throw new Error(`Badges request failed with status ${response.status}`);
  }
  return parseBadges(await response.json());
}

const queryDefaults: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
};

export function makeQueryClient(): QueryClient {
  return new QueryClient(queryDefaults);
}

// Query key includes the serialized params so each filter/page combination caches
// independently; invalidateAfterMutation's ['machine', 'approvals'] prefix still
// sweeps every variant (react-query partial key matching).
export function useApprovalsPage(query: ApprovalsQuery) {
  const search = approvalsSearch(query);
  return useQuery({
    queryKey: ['machine', 'approvals', search],
    queryFn: () => fetchApprovalsPage(query),
    placeholderData: (previous: ApprovalsPage | undefined) => previous,
  });
}

export function useBadges() {
  return useQuery({ queryKey: ['machine', 'badges'], queryFn: () => fetchBadges() });
}

export function objectivesQueryKey(range: ObjectivesRange | null): [string, string, string, string] {
  return ['machine', 'objectives', range?.start ?? 'default', range?.end ?? 'default'];
}

export function useObjectives(range: ObjectivesRange | null = null) {
  return useQuery({
    queryKey: objectivesQueryKey(range),
    queryFn: () => fetchObjectives(range),
  });
}

export function useStats() {
  return useQuery({ queryKey: ['machine', 'stats'], queryFn: () => fetchStats() });
}

export function useConfig() {
  return useQuery({ queryKey: ['machine', 'config'], queryFn: () => fetchConfig() });
}

export function useExperiments() {
  return useQuery({ queryKey: ['machine', 'experiments'], queryFn: () => fetchExperiments() });
}

export function useTactics() {
  return useQuery({ queryKey: ['machine', 'tactics', ''], queryFn: () => fetchTactics() });
}

export function useTacticExperiments() {
  return useQuery({
    queryKey: ['machine', 'tactic-experiments', ''],
    queryFn: () => fetchTacticExperiments(),
  });
}

export function useTacticDraftsPage(query: TacticDraftsQuery) {
  const search = tacticDraftsSearch(query);
  return useQuery({
    queryKey: ['machine', 'tactic-drafts', search],
    queryFn: () => fetchTacticDraftsPage(query),
    placeholderData: (previous: TacticDraftsPage | undefined) => previous,
  });
}

// Fetched separately from useStats so an item-stacks failure degrades only its own section,
// leaving MECE Coverage and TG Summary on screen.
export function useItemStacks() {
  return useQuery({ queryKey: ['machine', 'item-stacks'], queryFn: () => fetchItemStacks() });
}

export function useItemStatus() {
  return useQuery({ queryKey: ['machine', 'item-status'], queryFn: () => fetchItemStatus() });
}

export function useChangeLog() {
  return useQuery({ queryKey: ['machine', 'change-log'], queryFn: () => fetchChangeLog() });
}

export async function reverseTacticApplication(
  applicationId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<{ application_id: string; status: string }> {
  const response = await fetchImpl(path("/tactic-reverse"), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ application_id: applicationId }),
  });
  const body = (await response.json().catch(() => null)) as
    | { application_id?: string; status?: string; error?: string; detail?: string }
    | null;
  if (!response.ok) {
    throw new Error(body?.detail || body?.error || `reverse failed (${response.status})`);
  }
  return { application_id: body?.application_id ?? applicationId, status: body?.status ?? 'retired' };
}

export function useReverseTacticApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => reverseTacticApplication(applicationId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['machine', 'item-stacks'] }),
        queryClient.invalidateQueries({ queryKey: ['machine', 'item-status'] }),
        queryClient.invalidateQueries({ queryKey: ['machine', 'change-log'] }),
      ]);
    },
  });
}

export async function publishTacticApplication(
  applicationId: string,
  fetchImpl: FetchImpl = fetch,
): Promise<{ application_id: string; target_ref: string | null }> {
  const response = await fetchImpl(path("/tactic-publish"), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ application_id: applicationId }),
  });
  const body = (await response.json().catch(() => null)) as
    | { application_id?: string; target_ref?: string | null; error?: string; detail?: string }
    | null;
  if (!response.ok) {
    throw new Error(body?.detail || body?.error || `publish failed (${response.status})`);
  }
  return { application_id: body?.application_id ?? applicationId, target_ref: body?.target_ref ?? null };
}

export function usePublishTacticApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => publishTacticApplication(applicationId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['machine', 'item-stacks'] }),
        queryClient.invalidateQueries({ queryKey: ['machine', 'item-status'] }),
        queryClient.invalidateQueries({ queryKey: ['machine', 'change-log'] }),
      ]);
    },
  });
}

export async function invalidateAfterMutation(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['machine', 'approvals'] }),
    queryClient.invalidateQueries({ queryKey: ['machine', 'badges'] }),
  ]);
}

// --- C10 checks (C10-6) -----------------------------------------------------

export async function fetchChecks(
  fetchImpl: FetchImpl = fetch,
  opts: { fresh?: boolean } = {},
): Promise<MachineChecksSection> {
  const response = await fetchImpl(path(`/state/checks${opts.fresh ? '?fresh=1' : ''}`));
  if (response.status >= 400) {
    throw new Error(`Checks request failed with status ${response.status}`);
  }
  return parseChecksSection(await response.json());
}

export async function fetchCheckFindings(
  fetchImpl: FetchImpl = fetch,
  opts: { fresh?: boolean } = {},
): Promise<MachineCheckFindingsPage> {
  // `fresh=1` bypasses BOTH caches that would otherwise lie after a write: the cockpit
  // API's 60s in-process cache, and the browser's, since every state response carries
  // cache-control max-age=60 and a different URL is a different cache entry.
  const response = await fetchImpl(
    path(`/state/check-findings${opts.fresh ? '?fresh=1' : ''}`),
  );
  if (response.status >= 400) {
    throw new Error(`Check findings request failed with status ${response.status}`);
  }
  return parseCheckFindingsPage(await response.json());
}

export function useChecks() {
  return useQuery({ queryKey: ['machine', 'checks'], queryFn: () => fetchChecks() });
}

export function useCheckFindings() {
  return useQuery({ queryKey: ['machine', 'check-findings'], queryFn: () => fetchCheckFindings() });
}

export type FindingActionResult = { already_settled: boolean; payload: Record<string, unknown> };

export async function postFindingAction(
  path: string,
  body: Record<string, unknown>,
  fetchImpl: FetchImpl = fetch,
): Promise<FindingActionResult> {
  const response = await fetchImpl(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  // A finding someone already settled is NOT an error: the outcome the operator wanted is
  // already the outcome on the account. Saying so calmly beats a red alarm for a double click.
  if (response.status === 409 && payload && payload.error === 'finding_not_actionable') {
    return { already_settled: true, payload };
  }
  if (response.status >= 400 || !payload) {
    const detail = payload && typeof payload.detail === 'string' ? payload.detail : null;
    const code = payload && typeof payload.error === 'string' ? payload.error : `status ${response.status}`;
    throw new Error(detail ? `${code}: ${detail}` : code);
  }
  // Upstream returns 200 with result=failed when AMAZON refused. That is not a success.
  if (typeof payload.result === 'string' && payload.result !== 'ok') {
    const detail = typeof payload.error === 'string' ? payload.error : payload.result;
    throw new Error(`Amazon did not apply the fix: ${detail}`);
  }
  return { already_settled: false, payload };
}

function useFindingMutation(path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) => postFindingAction(path, { finding_id: findingId }),
    onSuccess: async () => {
      // Refetch FRESH, not merely invalidate. A plain invalidate re-requests the same URL,
      // which the browser answers from its own 60s cache - so the settled finding came back
      // still `open` and the buttons stayed live, inviting a second click.
      await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['machine', 'check-findings'],
          queryFn: () => fetchCheckFindings(fetch, { fresh: true }),
          staleTime: 0,
        }),
        queryClient.fetchQuery({
          queryKey: ['machine', 'checks'],
          queryFn: () => fetchChecks(fetch, { fresh: true }),
          staleTime: 0,
        }),
      ]);
    },
  });
}

export function useApproveCheckFinding() {
  return useFindingMutation(path("/check-approve"));
}

export function useDismissCheckFinding() {
  return useFindingMutation(path("/check-dismiss"));
}
